import React, { useContext, useEffect, useState } from "react";
import {
  Badge,
  Text,
  LegacyCard as Card,
  Checkbox,
  Collapsible,
  DataTable,
  Layout,
  PageActions,
  InlineStack,
  TextField,
} from "@shopify/polaris";
import { baseHelper, errorHelper } from "lib/helpers";
import { withErrorBoundary, withFeature } from "lib/hoc";
import { PrivateContext } from "lib/context";
import { Toast } from "lib/components";
import constant from "lib/constant/constant";
import { useMutation } from "@apollo/client";
import ExportTable from "app/payments/modules/generic/exportSetting/style";
import { StyledCardHeader } from "app/onboarding/modules/genericStyle";
import { UPDATE_PRODUCT_REPORT_SETTING } from "app/setup/apollo/mutations";
import { useNavigate } from "react-router-dom";

const { DOWN, gql, HIDE_CAPITAL, SHOW, UP } = constant;

const ProductExportReport = (props) => {
  const {
    CRITICAL,
    CUSTOMERPRODUCTPRICE,
    DISCOUNT,
    HIDDEN,
    MARKUP_KEY,
    PARTIALLY_HIDDEN,
    PAYMENT_TABLE_HEADING,
    PAYMENT_COLUMN,
    PRODUCTCOST,
    SUCCESS,
    VISIBLE,
    WARNING,
  } = constant;
  const {
    error: errorProductReportSetting,
    data: dataProductReportSetting,
    handleSetupToggleProductExport,
    openSetupProduct,
    productLastUpdateAt,
    refetch: productFetch,
    setBannerStatus,
  } = props;
  const { cms, currentUser } = useContext(PrivateContext);
  const navigate = useNavigate();
  const isVendor = baseHelper.isVendor(currentUser);
  const [submitEnabled, setSubmitEnable] = useState(false);
  const [firstPush, setFirstPush] = useState(true);
  const [message, setMessage] = useState("");
  // const [updatedAt, setUpdatedAt] = useState("");
  const [userInput, setUserInput] = useState({
    title: {
      label: "",
      isHideCsv: false,
      isHidePdf: false,
    },
    sku: {
      label: "",
      isHideCsv: false,
      isHidePdf: false,
    },
    productCost: {
      label: "",
      isHideCsv: false,
      isHidePdf: false,
    },
    productType: {
      label: "",
      isHideCsv: false,
      isHidePdf: false,
    },
    vendor: {
      label: "",
      isHideCsv: false,
      isHidePdf: false,
    },
    price: {
      label: "",
      isHideCsv: false,
      isHidePdf: false,
    },
    markup: {
      label: "",
      isHideCsv: false,
      isHidePdf: false,
    },
    customerProductPrice: {
      label: "",
      isHideCsv: false,
      isHidePdf: false,
    },
    discount: {
      label: "",
      isHideCsv: false,
      isHidePdf: false,
    },
    tax: {
      label: "",
      isHideCsv: false,
      isHidePdf: false,
    },
  });

  const [productSetting, { loading: settingUpdateLoading }] = useMutation(UPDATE_PRODUCT_REPORT_SETTING);

  useEffect(() => {
    if (dataProductReportSetting) {
      const productData = baseHelper.getResponseData(dataProductReportSetting, gql.GET_PRODUCT_REPORT_SETTING);
      const productError = baseHelper.getResponseError(errorProductReportSetting, gql.GET_PRODUCT_REPORT_SETTING);
      const data = productData?.report ?? {};
      const next = { ...data }
      if (next) {
        const keys = Object.keys(userInput);
        keys.forEach((key) => {
          if (!next[key]) {
            next[key] = {
              label: "",
              isHideCsv: false,
              isHidePdf: false,
            };
          }
        });
        if (productData.report) {
          // setUpdatedAt(productData.updatedAt);
          setFirstPush(false);
          setUserInput(next);
        }
      }
      if (productError) {
        setBannerStatus({ isOpen: true, title: productError, status: constant.CRITICAL });
      }
    }
  }, [dataProductReportSetting, errorProductReportSetting]);

  useEffect(() => {
    setTimeout(() => {
      setMessage("");
    }, 2000);
  }, [message]);

  const verifyValues = () => {
    setSubmitEnable(false);
    const newData = [];
    const noUncheckCsv = Object.keys(userInput)
      .filter((item) => {
        return isVendor
          ? !(
            isVendor &&
            (item === DISCOUNT || item === CUSTOMERPRODUCTPRICE || item === PRODUCTCOST || item === MARKUP_KEY)
          )
          : true;
      })
      .map((item) => {
        newData.push(userInput[item]);
        if (userInput[item].isHideCsv) {
          return true;
        }
        return false;
      });
    const noUncheckPdf = Object.keys(userInput)
      .filter((item) => {
        return isVendor
          ? !(
            isVendor &&
            (item === DISCOUNT || item === CUSTOMERPRODUCTPRICE || item === PRODUCTCOST || item === MARKUP_KEY)
          )
          : true;
      })
      .map((item) => {
        if (userInput[item].isHidePdf) {
          return true;
        }
        return false;
      });
    let response = false;
    newData.forEach((item, i) => {
      newData.forEach((element, index) => {
        if (i === index) return null;
        if (element.label && item.label && element.label.trim() === item.label.trim()) {
          response = true;
          setBannerStatus({
            isOpen: true,
            title: cms("message.error.duplicate"),
            status: constant.CRITICAL,
          });
          return false;
        }
        return false;
      });
    });
    if (!response) {
      response = true;
      noUncheckCsv.forEach((item) => {
        if (!item) response = item;
      });
      setBannerStatus({
        isOpen: response,
        title: cms("message.error.uncheckCsv"),
        status: constant.CRITICAL,
      });
    }
    if (!response) {
      response = true;
      noUncheckPdf.forEach((item) => {
        if (!item) response = item;
      });
      setBannerStatus({
        isOpen: response,
        title: cms("message.error.uncheckPdf"),
        status: constant.CRITICAL,
      });
    }
    return response;
  };

  const updateProductSetting = async (type) => {
    productSetting({ variables: { input: { ...userInput } } })
      .then((res) => {
        if (res) {
          const resData = baseHelper.getResponseData(res.data, constant.gql.UPDATE_PRODUCT_REPORT_SETTING);
          const resError = baseHelper.getResponseError(res.data, constant.gql.UPDATE_PRODUCT_REPORT_SETTING);
          if (resData) {
            productFetch();
            setMessage(cms("message.success.productUpdate"));
          }
          if (type === "save") {
            setMessage(cms("message.success.productSave"));
          }
          if (resError) {
            setBannerStatus({ isOpen: true, title: resError, status: constant.CRITICAL });
          }
        }
      })
      .catch((exception) => {
        setBannerStatus({ isOpen: true, title: errorHelper.parse(exception), status: constant.CRITICAL });
      });
  };

  const handleLabelChange = (val, key) => {
    const updatedData = JSON.parse(JSON.stringify(userInput));
    const newData = updatedData[key];
    newData.label = val;
    setUserInput({ ...updatedData, [key]: newData });
    setSubmitEnable(true);
  };

  const selectCurrentLabelsColumn = (item) => <Text as="span">{cms(`label.${item}`) || item}</Text>;
  const selectNewLabelsTextField = (data, key) => (
    <>
      <TextField value={data && data.label} onChange={(value) => handleLabelChange(value, key)} />
    </>
  );

  const handleCheckbox = (value, key) => {
    const updateData = userInput[key];
    setSubmitEnable(true);
    if (value === "isCsv") setUserInput({ ...userInput, [key]: { ...updateData, isHideCsv: !updateData.isHideCsv } });
    if (value === "isPdf") setUserInput({ ...userInput, [key]: { ...updateData, isHidePdf: !updateData.isHidePdf } });
  };

  const badges = (item) => (
    <>
      <Badge tone={item.badgesStatus}>{item.badgesLable}</Badge>
    </>
  );
  const selectHideColumn = (index) => (
    <>
      <InlineStack gap="400" align="center">
        <Checkbox
          label={cms("label.isCsv")}
          checked={userInput[index] && userInput[index].isHideCsv}
          onChange={() => handleCheckbox("isCsv", index)}
        />
        <Checkbox
          label={cms("label.isPdf")}
          checked={userInput[index] && userInput[index].isHidePdf}
          onChange={() => handleCheckbox("isPdf", index)}
        />
      </InlineStack>
    </>
  );

  const getRows = () => {
    return Object.keys(userInput)
      .filter((item) => {
        return isVendor
          ? !(
            isVendor &&
            (item === DISCOUNT || item === CUSTOMERPRODUCTPRICE || item === PRODUCTCOST || item === MARKUP_KEY)
          )
          : true;
      })
      .map((item) => {
        let badge = { badgesLable: VISIBLE, badgesStatus: SUCCESS };
        const itemData = userInput[item];
        if (itemData.isHideCsv && itemData.isHidePdf) {
          badge = { badgesLable: HIDDEN, badgesStatus: CRITICAL };
        } else if (itemData.isHidePdf || itemData.isHideCsv) {
          badge = { badgesLable: PARTIALLY_HIDDEN, badgesStatus: WARNING };
        } else {
          badge = { badgesLable: VISIBLE, badgesStatus: SUCCESS };
        }
        const currentValues = selectCurrentLabelsColumn(item);
        const newValues = selectNewLabelsTextField(userInput[item], item);
        const badgesValues = badges(badge);
        const hideColumnValues = selectHideColumn(item);
        return [[currentValues], [newValues], [hideColumnValues], [badgesValues]];
      });
  };

  return (
    <Layout.AnnotatedSection
      title={cms("section.productExport.title")}
      description={cms("section.productExport.description")}
    >
      <Card>
        <StyledCardHeader className="styled-card-header">
          <Card.Header
            sectioned
            title={
              // eslint-disable-next-line react/jsx-wrap-multilines
              <InlineStack>
                <Text variant="headingMd" as="h2">
                  {cms("section.productExport.cardTitle")}
                  <Text as="span" tone="subdued">
                    {productLastUpdateAt && (
                      <Text as="span" tone="subdued">
                        <Text variant="bodySm" as="p">{`${baseHelper.lastUpdateDate(productLastUpdateAt)}`}</Text>
                      </Text>
                    )}
                  </Text>
                  <Text tone="subdued" />
                </Text>
              </InlineStack>
            }
            actions={[
              {
                content: openSetupProduct ? HIDE_CAPITAL : SHOW,
                onAction: () => handleSetupToggleProductExport(),
                disclosure: openSetupProduct ? UP : DOWN,
              },
            ]}
          />
        </StyledCardHeader>
      </Card>
      <br />
      <Collapsible open={openSetupProduct} id="basic-collapsible">
        <Card>
          <Card.Section>
            <ExportTable className="payment-export-table">
              <DataTable columnContentTypes={PAYMENT_COLUMN} headings={PAYMENT_TABLE_HEADING} rows={getRows()} />
            </ExportTable>
            {!firstPush && (
              <div className="page-action">
                <PageActions
                  primaryAction={{
                    content: cms("common.button.update"),
                    onAction: () => {
                      const update = verifyValues();
                      if (!update) updateProductSetting();
                    },
                    disabled: !submitEnabled,
                    loading: settingUpdateLoading,
                  }}
                  secondaryActions={[
                    {
                      id: "cancelButton",
                      content: cms("common.button.cancel"),
                      onAction: () => navigate("/setting"),
                    },
                  ]}
                />
              </div>
            )}
          </Card.Section>
        </Card>
        {firstPush && (
          <div>
            <PageActions
              primaryAction={{
                content: cms("common.button.submit"),
                onAction: () => {
                  const update = verifyValues();
                  if (!update) updateProductSetting("save");
                },
                disabled: !submitEnabled,
                loading: settingUpdateLoading,
              }}
              secondaryActions={[
                {
                  id: "cancelButton",
                  content: cms("common.button.cancel"),
                  onAction: () => navigate("/setting"),
                },
              ]}
            />
          </div>
        )}
      </Collapsible>
      <div>
        <Toast message={message} timeout={5000} />
      </div>
    </Layout.AnnotatedSection>
  );
};

export default withFeature(withErrorBoundary(ProductExportReport), {
  // feature: constant.REPORT_SETTING,
  feature: constant.PRODUCT_REPORT_SETTING,
});
